/**
 * Joining two points, which is the whole of building a bench.
 *
 * In the engine rather than the surface because it is a statement about the
 * arrangement, not about clicking: given two points, what does the world
 * become? That makes it something jest can hold to the simple cases, which is
 * the only way a rule with this many branches stays right.
 *
 * A JOIN IS TWO POINTS. Which two decides the kind, and the rule is three
 * lines because a port has a ROLE and a kind says whether it may be bolted:
 *
 *   two anchorages, both bondable → the parts are bolted together
 *   either one a guide           → a rope run over that sheave
 *   either part not bondable     → a rope, because a rope end is a loose end
 *
 * A rope over a sheave is therefore two joins — the weight to the rim, then
 * that rim to wherever the rope ends — and in between it is a real,
 * half-finished rope: one that runs over a sheave and stops there. That state
 * is not stored anywhere, because it is visible in the path. See `openEndOf`.
 */
import { defOf } from './registry';
import { stands } from './settle';
import { JoinKind, Link, Part, PortRef, Rope, Vec2, World } from './types';
import { bodiesOf, bondsOf, extentAlong, isFree, measureRope, measureSpans, partById, portAt, portsOf, ropesOf, slackOf, supportOf } from './world';

/** A rope's length is what it measured when it was threaded — so every edit
 *  that moves anything re-measures, and running it never does. */
/**
 * Put everything standing on a face back ON its face.
 *
 * The companion to re-measuring a rope, and for the same reason: a face is
 * geometry the parts standing on it depend on, so anything that changes that
 * geometry has to put them back. Make a block heavier and it grows; shorten
 * the base or steepen a slope and the face moves out from under it. Left
 * alone, the block overhangs — and an overhanging block is a violated edge row
 * sitting at rest, which over-determines the very coordinate the rope is
 * pulling on and reports the whole bench as having no answer.
 */
function reseat(w: World): World {
    return {
        ...w,
        parts: w.parts.map(p => {
            const on = supportOf(w, p);
            if (!on) return p;
            const clear = extentAlong(w, p, { x: -on.axes[1].x, y: -on.axes[1].y });
            const room = Math.max(0, on.half - extentAlong(w, p, on.axes[0]));
            const s = Math.max(-room, Math.min(room, on.along(p.at)));
            return { ...p, at: on.seat(s, clear) };
        }),
    };
}

/**
 * A rope that has not been measured yet.
 *
 * Every rope is created with `length: 0` — a placeholder, because how much
 * rope it took cannot be known until the path exists and the parts are
 * somewhere. Zero is not a length a real rope can have, so it says "not yet"
 * without needing a second field to say it.
 */
const unmeasured = (l: Rope): boolean => !(l.length > 0);

/**
 * Seat whatever rests on a surface, and measure any rope that has just been
 * threaded.
 *
 * ONCE, and that is the whole of the change. This used to re-measure every
 * rope on every edit, which meant a rope was however long the drawing
 * currently needed it to be — see `Rope.length`. Now a rope gets its number
 * when it is threaded and keeps it, and an edit that would need more rope than
 * there is has to be refused instead of quietly granted.
 */
export const remeasure = (w0: World): World => ((w: World) => ({
    ...w,
    links: w.links.map(l => {
        if (l.type !== 'rope' || !unmeasured(l)) return l;
        /* Both the total and the span-by-span breakdown. The spans are what the
         * solver reads; the total is what a reader is shown and what a rope
         * built by hand carries on its own. */
        const lengths = measureSpans(w, l);
        return { ...l, length: lengths.reduce((a, b) => a + b, 0), lengths };
    }),
}))(reseat(w0));

/**
 * Measure a rope that has just been threaded further, keeping the surplus.
 *
 * How much SPARE rope a student asked for is a property of the rope; where it
 * runs is a property of the path. Carrying it on to another stop changes the
 * second and should not silently discard the first.
 */
const rethread = (w: World, ropeId: string, spare: number): World => {
    const measured = remeasure(w);
    if (spare <= 1e-9) return measured;
    return {
        ...measured,
        links: measured.links.map(l => (l.id === ropeId && l.type === 'rope'
            ? { ...l, length: l.length + spare, lengths: undefined }
            : l)),
    };
};

const roleOf = (w: World, ref: PortRef): 'anchorage' | 'guide' | undefined => portAt(w, ref)?.role;

const samePort = (a: PortRef, b: PortRef): boolean => a.partId === b.partId && a.portId === b.portId;

/** What kind of join a link is. */
export const joinKindOf = (l: Link): JoinKind => (l.type === 'bond' ? 'bond' : l.rigid ? 'rod' : 'rope');

/** Every join that meets at this point. A rope counts once, however it runs. */
export function joinsAt(w: World, ref: PortRef): Link[] {
    return w.links.filter(l => (l.type === 'rope' ? l.path : [l.hangs, l.holds]).some(s => samePort(s, ref)));
}

/**
 * Has this point room for one more join of this kind?
 *
 * `except` leaves one link out of the count — the one being changed, when the
 * question is whether it may become a different kind where it already is.
 */
export function canTake(w: World, ref: PortRef, kind: JoinKind, except?: string): boolean {
    const port = portAt(w, ref);
    if (!port || !port.joins.takes.includes(kind)) return false;
    return joinsAt(w, ref).filter(l => l.id !== except).length < port.joins.max;
}

/**
 * Is this rope unfinished, and where?
 *
 * A rope whose last stop is a GUIDE does not end there — it runs over that
 * sheave and goes on to somewhere the student has not said yet. Derived rather
 * than stored: a flag would be a second answer to a question the path already
 * answers, and the two would eventually disagree.
 */
export function openEndOf(w: World, rope: Rope): { at: PortRef; atEnd: boolean } | undefined {
    const last = rope.path[rope.path.length - 1];
    if (rope.path.length && roleOf(w, last) === 'guide') return { at: last, atEnd: true };
    const first = rope.path[0];
    if (rope.path.length > 1 && roleOf(w, first) === 'guide') return { at: first, atEnd: false };
    return undefined;
}

/** A rope waiting to be carried on from this part. */
export function openRopeAt(w: World, partId: string): { rope: Rope; atEnd: boolean } | undefined {
    for (const rope of ropesOf(w)) {
        const open = openEndOf(w, rope);
        if (open && open.at.partId === partId) return { rope, atEnd: open.atEnd };
    }
    return undefined;
}

/** A rope end is a loose end by definition, so joining from one carries its
 *  rope on rather than starting a second rope at the same point. */
export function looseEndOf(w: World, part: Part): { rope: Rope; atEnd: boolean } | undefined {
    if (defOf(part).bondable) return undefined;
    for (const rope of ropesOf(w)) {
        if (rope.path[rope.path.length - 1]?.partId === part.id) return { rope, atEnd: true };
        if (rope.path[0]?.partId === part.id) return { rope, atEnd: false };
    }
    return undefined;
}

/** How near a dragged point must come to another to join it when let go, in metres. */
export const SNAP_RADIUS = 0.3;

/** A join a drag could make: this point of the dragged part, to that one. */
export interface Snap { from: PortRef; to: PortRef; kind: JoinKind; at: Vec2 }

/**
 * Everywhere a dragged part could join on, asked once when the drag starts.
 *
 * Only from points that declare `snaps`, and only to points the join rule
 * accepts — the same `canJoin` the dots obey, so a drop can never make a join
 * a click could not. Not to a part it is already joined to: a rope end dragged
 * back to the hook its rope starts from is being moved, not tied twice.
 */
export function snapTargets(w: World, partId: string): Snap[] {
    const part = partById(w, partId);
    if (!part) return [];
    const joined = new Set<string>();
    w.links.forEach(l => {
        const stops = l.type === 'rope' ? l.path : [l.hangs, l.holds];
        if (stops.some(s => s.partId === partId)) stops.forEach(s => joined.add(s.partId));
    });
    const bolted = bondsOf(w).some(b => b.hangs.partId === partId || b.holds.partId === partId);
    const out: Snap[] = [];
    for (const port of portsOf(w, part)) {
        const kind = port.joins.snaps;
        // Something already bolted is moved by a drag, not bolted again.
        if (!kind || (kind === 'bond' && bolted)) continue;
        const from = { partId, portId: port.id };
        for (const other of w.parts) {
            if (other.id === partId || joined.has(other.id)) continue;
            for (const target of portsOf(w, other)) {
                // Two points that snap the SAME way — two rope ends, two axles —
                // are each something being placed, not somewhere to place it.
                // A rope end onto an axle is a rope tied to a pulley's centre.
                if (target.joins.snaps === kind) continue;
                const to = { partId: other.id, portId: target.id };
                const ok = kind === 'bond'
                    // A bolt is made as a join and retied, so both must take both.
                    ? defOf(part).bondable && defOf(other).bondable
                        && canTake(w, to, 'bond') && canJoin(w, to, from)
                    : canJoin(w, from, to);
                if (ok) out.push({ from, to, kind, at: target.at });
            }
        }
    }
    return out;
}

/**
 * Everywhere a rope pulled out FROM this point could be joined — what a drag
 * from a pulley's rope piece lights up. `canJoin`, so a drop makes only a join
 * a click could: a hook, a block, a rope's loose end, another wheel's rim.
 */
export function joinTargets(w: World, from: PortRef): Snap[] {
    const out: Snap[] = [];
    for (const other of w.parts) {
        if (other.id === from.partId) continue;
        for (const target of portsOf(w, other)) {
            const to = { partId: other.id, portId: target.id };
            if (canJoin(w, from, to)) out.push({ from, to, kind: 'rope', at: target.at });
        }
    }
    return out;
}

/** The target nearest a point, if one is in reach. */
export function nearestTarget(targets: Snap[], at: Vec2, radius = SNAP_RADIUS): Snap | undefined {
    let best: Snap | undefined;
    let reach = radius;
    for (const s of targets) {
        const d = Math.hypot(at.x - s.at.x, at.y - s.at.y);
        if (d <= reach) { best = s; reach = d; }
    }
    return best;
}

/** The target nearest the dragged point, if the part were at `at` and one is in reach. */
export function snapNear(w: World, targets: Snap[], partId: string, at: Vec2, radius = SNAP_RADIUS): Snap | undefined {
    const part = partById(w, partId);
    if (!part) return undefined;
    const dx = at.x - part.at.x, dy = at.y - part.at.y;
    let best: Snap | undefined;
    let reach = radius;
    for (const s of targets) {
        const p = portAt(w, s.from);
        if (!p) continue;
        const d = Math.hypot(p.at.x + dx - s.at.x, p.at.y + dy - s.at.y);
        if (d <= reach) { best = s; reach = d; }
    }
    return best;
}

/**
 * Can a rope be carried on FROM this point? A rim a rope is left hanging over,
 * or a rope's own loose end — both are full, and both are where a join starts.
 */
export function carriesFrom(w: World, ref: PortRef): boolean {
    if (ropesOf(w).some(r => { const o = openEndOf(w, r); return !!o && samePort(o.at, ref); })) return true;
    const part = partById(w, ref.partId);
    return !!part && !!looseEndOf(w, part);
}

/**
 * Which of two bolted parts hangs from the other.
 *
 * A bond has a direction, and it is not decoration: the drawing hangs the
 * strap off whatever carries it. Recorded the wrong way round — a ceiling hook
 * hanging from a pulley — the physics still comes out right, because a body
 * holding an anchor is pinned either way, and NOTHING IS DRAWN. Joining a hook
 * to an axle then looks like it did nothing at all.
 *
 * What cannot move holds; otherwise the lower part hangs from the upper one,
 * which is what a bench full of gravity means by "hangs from".
 */
function hanger(w: World, a: Part, b: Part): [Part, Part] {
    const pinned = (p: Part): boolean => defOf(p).freedom(p as never, w).axes.length === 0;
    if (pinned(a) && !pinned(b)) return [b, a];
    if (pinned(b) && !pinned(a)) return [a, b];
    return a.at.y <= b.at.y ? [a, b] : [b, a];
}



/**
 * Is there a join from this point to that one? The same rule `joinPorts`
 * makes the join by, without solving the bench it would leave — cheap enough
 * to ask of every point on the bench, which is what a drag looking for
 * somewhere to land has to do.
 */
export const canJoin = (w: World, a: PortRef, b: PortRef): boolean => !!planJoin(w, a, b, k => k);

/** The whole join, as one pure step on the world. A join the points cannot
 *  take leaves the world as it was. */
export function joinPorts(w: World, a: PortRef, b: PortRef, mint: (kind: string) => string): World {
    const planned = planJoin(w, a, b, mint);
    if (!planned) return w;
    const made = new Set(w.links.map(l => l.id));
    return stands(hangPlumb(planned, planned.links.filter(l => !made.has(l.id)).map(l => l.id)));
}

/**
 * A block just hung on a rope hangs STRAIGHT DOWN from what it hangs from.
 *
 * A join measures a rope where it was laid out, which is right for how much
 * rope there is and wrong for where a block on it would be: tie a block that
 * happened to be standing off to one side and its rope was drawn — and kept —
 * at that slant. When the bench can hang still, settling moves it plumb; when
 * it cannot (the tensions on one rope disagree, say), nothing does, and the
 * slant is the only answer on screen. That is a rope no block has ever hung on.
 *
 * So: a two-point rope, one end a block free to move, resting on nothing,
 * held by this rope and nothing else, the other end above it — the block goes
 * under that point, the same drop, and the rope is measured again there.
 * Anything else is left exactly as it was laid out.
 */
export function hangPlumb(w: World, ropeIds: string[]): World {
    let out = w;
    for (const id of ropeIds) {
        const rope = out.links.find(l => l.id === id);
        if (!rope || rope.type !== 'rope' || rope.path.length !== 2) continue;
        for (const [end, other] of [[rope.path[0], rope.path[1]], [rope.path[1], rope.path[0]]] as [PortRef, PortRef][]) {
            const block = partById(out, end.partId);
            const from = portAt(out, other), at = portAt(out, end);
            if (!block || !from || !at || block.kind !== 'weight' || block.support) continue;
            if (defOf(block).freedom(block as never, out).axes.length === 0) continue;
            if (joinsAt(out, end).length !== 1 || out.links.some(l => l.id !== id && (l.type === 'rope' ? l.path : [l.hangs, l.holds]).some(s => s.partId === block.id))) continue;
            if (from.at.y <= at.at.y + 1e-9) continue;
            const dx = from.at.x - at.at.x;
            if (Math.abs(dx) < 1e-9) break;
            out = {
                ...out,
                parts: out.parts.map(p => (p.id === block.id ? { ...p, at: { x: p.at.x + dx, y: p.at.y } } : p)),
                links: out.links.map(l => {
                    if (l.id !== id || l.type !== 'rope') return l;
                    const { lengths: _spans, ...rest } = l;
                    void _spans;
                    return { ...rest, length: 0 };
                }),
            };
            break;
        }
    }
    return out;
}

function planJoin(w: World, a: PortRef, b: PortRef, mint: (kind: string) => string): World | undefined {
    const A = partById(w, a.partId), B = partById(w, b.partId);
    if (!A || !B || A.id === B.id || samePort(a, b)) return undefined;

    /* Everything a rope already holds, read BEFORE the join — the whole point
     * is what was true before this one was made. */
    const tied = new Set<string>();
    ropesOf(w).forEach(r => r.path.forEach(stop => tied.add(stop.partId)));

    /* Two tie-off points make a ROPE, and the reader retie it to a rod or a
     * bolt if that is what they meant.
     *
     * It used to make a bolt, on the grounds that two anchorages clicked
     * together is a bolt-shaped gesture. But a rope is what a reader means far
     * more often — a pulley hung from a hook hangs on a rope — and it is the
     * one of the three that can be wrong in the safe direction: a rope that
     * should have been rigid goes slack and says so, where a bolt that should
     * have been a rope silently welds two parts and the bench reports no gap
     * to read. Changing it afterwards is one click either way, and the panel
     * says what the three are for.
     *
     * Presets are unaffected: they declare their links as data, so a bench
     * that is meant to be bolted still is. */

    // Carrying on: either point may be the open one, so both orders are tried.
    for (const [p, q] of [[a, b], [b, a]] as [PortRef, PortRef][]) {
        const P = partById(w, p.partId) as Part;
        const carry = roleOf(w, p) === 'guide' ? openRopeAt(w, p.partId) : looseEndOf(w, P);
        if (!carry) continue;
        const { rope, atEnd } = carry;
        /* The point it is carried ON to must have room for it — unless it is a
           rim another rope is already left hanging over, because then the two
           become one rope over that rim, not a second rope on it. */
        const merging = roleOf(w, q) === 'guide'
            && ropesOf(w).some(l => l.id !== rope.id && openEndOf(w, l)?.at.partId === q.partId);
        if (!merging && !canTake(w, q, joinKindOf(rope))) return undefined;
        // A rope end is CONSUMED by being carried on from — it was the end and
        // now it is not. A rim stays, because the rope goes over it and keeps
        // going.
        const drop = !defOf(P).bondable;
        const core = atEnd
            ? (drop ? rope.path.slice(0, -1) : rope.path)
            : (drop ? rope.path.slice(1) : rope.path);
        const path = atEnd ? [...core, q] : [q, ...core];
        /* Carrying a rope on to a new stop is THREADING MORE ROPE — the one
         * thing that is genuinely allowed to change how much rope there is.
         * Dragging a part is not, which is the whole of `Rope.length` being
         * authored; but a rope that now runs somewhere it did not run before
         * has to be measured again, or it keeps the length of a path it no
         * longer has. Marked unmeasured here; `remeasure` does it below. */
        const spare = slackOf(w, rope);
        const next: Rope = { ...rope, path, length: 0, lengths: undefined };
        const links = w.links.map(l => (l.id === rope.id ? next : l));
        const gone = drop && !links.some(l => l.type === 'rope' && l.path.some(s => s.partId === P.id));
        /* Line the new stop up with the rim the rope LEAVES from.
         *
         * That rim is the mirror of the one the rope is OPEN at — which is not
         * the same as the mirror of whichever rim was clicked. A rope hanging
         * on the left rim is carried on by clicking the RIGHT one, and mirroring
         * that gives the left rim back: the new stop was then lined up under
         * the leg already hanging there, landing a second hook exactly on top
         * of the first. Two hooks in one place look like one hook with a
         * slanted rope, which is the wrong arrangement drawn convincingly.
         *
         * `openEndOf` already knows which stop is open; ask the rope, not the
         * click. It is also the same rule `segmentsOf` measures by. */
        let carried = { ...w, parts: gone ? w.parts.filter(x => x.id !== P.id) : w.parts, links };
        let spliced = spare;

        /* A rope landing where ANOTHER rope is already open is the same rope.
         *
         * Threading order should not decide how many ropes a bench has. Run a
         * line from a hook over one pulley to a second, having already hung the
         * far leg off that second pulley, and what the student has plainly made
         * is one rope over two wheels — but each join only ever extended the
         * rope it started from, so the bench kept two, both unfinished, both
         * complaining that they ran over a pulley and stopped there.
         *
         * Only at a RIM. Two ropes tied to the same hook are two ropes; a rope
         * does not pass THROUGH an anchorage. It passes over a wheel, and a
         * wheel with an open end of each is a wheel with one rope over it. */
        if (roleOf(carried, q) === 'guide') {
            const other = ropesOf(carried).find(l => l.id !== rope.id
                && openEndOf(carried, l)?.at.partId === q.partId);
            if (other) {
                const o = openEndOf(carried, other) as { at: PortRef; atEnd: boolean };
                // Its remaining stops, ordered leading AWAY from the shared wheel.
                const rest = o.atEnd
                    ? other.path.slice(0, -1).reverse()
                    : other.path.slice(1);
                const joined = atEnd ? [...path, ...rest] : [...[...rest].reverse(), ...path];
                spliced += slackOf(carried, other);
                carried = {
                    ...carried,
                    links: carried.links
                        .filter(l => l.id !== other.id)
                        .map(l => (l.id === rope.id ? { ...next, path: joined } : l)),
                };
            }
        }

        /* Nothing is straightened, centred or lined up here any more.
         *
         * Three helpers used to run at this point — `align` to make a new run
         * vertical, `centreUnder` to hang a sling symmetrically — each of them
         * deciding a part's sideways position by a rule about the drawing,
         * because nothing could work it out. `stands` works it out: a join
         * changes what holds what, the statics is solved again, and every
         * part lands where the forces put it. A sling comes out symmetric
         * because the two legs pull alike, which is a better reason than
         * having been placed that way. */
        return rethread(carried, rope.id, spliced);
    }

    if (!canTake(w, a, 'rope') || !canTake(w, b, 'rope')) return undefined;
    const rope: Rope = { id: mint('rope'), type: 'rope', path: [a, b], length: 0 };
    return { ...w, links: [...w.links, rope] };
}
